import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { HttpService } from '../../common/http/http.service';
import { IApiResponse } from '../../../dtos/common/api-response.dto';
import { STORAGE_KEYS } from '../../../constants/storage.keys';
import { JoinRequestDto } from '../../../dtos/auth/join-request.dto';
import { ForgotPasswordDto } from '../../../dtos/auth/forgot-password.dto';
import { ResetPasswordDto } from '../../../dtos/auth/reset-password.dto';
import { AuthResponse } from '../../../dtos/get/auth.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private platformId = inject(PLATFORM_ID);

  private currentUserSubject = new BehaviorSubject<any | null>(this.getStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpService) {}

  login(payload: { email: string; password: string }): Observable<IApiResponse<AuthResponse>> {
    return this.http.post<IApiResponse<AuthResponse>, any>('auth/login', payload).pipe(
      tap((res) => {
        if (res.success && res.data) {
          this.handleAuthSuccess(res.data);
        }
      }),
    );
  }

  join(payload: JoinRequestDto): Observable<IApiResponse<boolean>> {
    return this.http.post<IApiResponse<boolean>, JoinRequestDto>('auth/join', payload);
  }

  checkEmail(email: string): Observable<IApiResponse<boolean>> {
    return this.http.post<IApiResponse<boolean>, null>(
      `auth/check-email?email=${encodeURIComponent(email)}`,
      null,
    );
  }

  forgotPassword(payload: ForgotPasswordDto): Observable<IApiResponse<any>> {
    return this.http.post<IApiResponse<any>, ForgotPasswordDto>('auth/forgot-password', payload);
  }

  resetPassword(payload: ResetPasswordDto): Observable<IApiResponse<any>> {
    return this.http.post<IApiResponse<any>, ResetPasswordDto>('auth/reset-password', payload);
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN_KEY);
    localStorage.removeItem(STORAGE_KEYS.USER_KEY);

    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    return localStorage.getItem(STORAGE_KEYS.TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUser(): any | null {
    return this.getStoredUser();
  }

  private handleAuthSuccess(data: AuthResponse): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN_KEY, data.token);
    localStorage.setItem(STORAGE_KEYS.USER_KEY, JSON.stringify(data.user));

    this.currentUserSubject.next(data.user);
  }

  private getStoredUser(): any | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const user = localStorage.getItem(STORAGE_KEYS.USER_KEY);
    return user ? JSON.parse(user) : null;
  }
}
